<script>
import constants from "@/libs/Constants";
import ApiConnection from "@/libs/ApiConnection";
import {localeToFuncLocale} from "@/libs/locales";
import VueDatePicker from "@vuepic/vue-datepicker";
import ModalWindow from "@/components/ModalWindow.vue";
import GroupErrorTranslation from "@/libs/locales/errors";
import LineTimeSeries from "@/components/Charts/LineTimeSeries.vue";
import {VisualizationMonitoringData} from "@/libs/VisualizationData";
import {
  getVisualizationComponentI18NLabel,
  UUIDfromList,
  yearMonthToDate,
  yearMonthStrToMonthYearI18N,
  yearMonthToStr,
  downloadFileFromAElement,
  disposeTooltipsByClass,
  setupTooltipsByClass
} from "@/libs/Helpers";
import OffCanvas from "@/components/OffCanvas.vue";

export default {
  name: 'PlotMonitoring',
  components: {
    OffCanvas,
    VueDatePicker,
    ModalWindow,
    LineTimeSeries
  },
  emits: ['onCloseModalClick', 'onOpenModal', 'onTimeSeriesDataError', 'afterPlotDataLoading'],
  props: {
    propPlotLayers: Array || null,
    propPlotLayerNames: {type: Array, default: []},
    propPlotLayersCount: {type: Number, default: null},
    propPlotLayersTrigger: Boolean,
    propModalStatus: Boolean,
    propFormData: VisualizationMonitoringData || null,
  },
  data() {
    return {
      localeToFuncLocale,
      constants: constants,
      plotData: null, //Data from API
      lastDataUUID: null, //UUID for data only for last month

      loading: {
        data: false, //Flag for data loading from API
        file: false,
        plot: false
      },
      offCanvasAreas: {
        show: false
      },
      modalStatus: false, //Status for modal to show
      plotDownload: false,

      //Form
      currentDateStart: null,
      currentDateEnd: null,
      dateRange: null,
    }
  },
  watch: {
    propModalStatus(newValue, oldValue) {
      if (this.modalStatus !== newValue)
        this.modalStatus = newValue;
    },
    propPlotLayersTrigger(newValue, oldValue) {
      if (this.propFormData !== null && this.propPlotLayers !== null && this.propPlotLayers.length > 0) {
        /***
         * To avoid a request of data using the same parameters, the idea
         * is to build a UUID of the parameters (including the selected features)
         * and check whether the data is already present in the time-series or not.
         *
         * If the last UUID corresponds to the new one, then this method will only
         * send the signal to open the modal with data already loaded, otherwise
         * it makes the API request and sends the retrieved data.
         ***/
        let uuidList = [
          this.propFormData.component,
          this.propFormData.variable,
          this.propFormData.yearMonthStr,
          this.propPlotLayers.sort().join(','),
          this.$i18n.locale // Forces data to reload when language changes outside the component
        ];

        let newUUID = UUIDfromList(uuidList);

        if (this.plotData === null || this.lastDataUUID === null || this.lastDataUUID !== newUUID) {
          this.loading.data = true;

          //Set initial date range as 12 months before selected date
          let dateEnd = yearMonthToDate(this.propFormData.yearMonthStr);
          let dateStart = new Date(dateEnd);

          dateStart.setMonth(dateStart.getMonth() - 11);

          this.currentDateStart = {
            month: dateStart.getMonth(),
            year: dateStart.getFullYear(),
          };

          this.currentDateEnd = {
            month: dateEnd.getMonth(),
            year: dateEnd.getFullYear(),
          };

          this.dateRange = [this.currentDateStart, this.currentDateEnd];

          ApiConnection.postMonitoringTimeseries(
              this.propFormData.variable,
              this.dateRangeStartStr,
              this.dateRangeEndStr,
              this.propPlotLayers
          ).then(response => {
            response.json().then(output => {
              //Update plot data
              this.plotData = output;

              //Update last UUID
              this.lastDataUUID = newUUID;

              //Open modal
              this.modalOpen();
            });
          }).catch((error) => {
            //Send error to parent
            this.$emit('onTimeSeriesDataError', GroupErrorTranslation.t(error.message, this.$i18n));
          }).finally(() => {
            this.loading.data = false;
            this.$emit('afterPlotDataLoading');
          });
        } else {
          this.$emit('afterPlotDataLoading');

          //Open modal
          this.modalOpen();
        }

        this.$refs.datePickerRange.parseModel();

        this.$nextTick(() => {
          this.tooltipsSetup();
          let object = document.getElementById('monitoring-plot-offcanvas-link');
          if (object !== undefined && object !== null) {
            object.removeEventListener('click', this.offCanvasAreasShowOnClick);
            object.addEventListener('click', this.offCanvasAreasShowOnClick);
          }
        });
      }
    },
  },
  computed: {
    monthConfiguration() {
      if (this.propFormData !== undefined && this.propFormData !== null)
        return this.propFormData.monthConfiguration;
      return undefined;
    },
    variableAvailableDates() {
      let variableData = this.monthConfiguration;
      return variableData !== undefined ? variableData.availableDates : [];
    },
    variableMissingDates() {
      let variableData = this.monthConfiguration;
      return variableData !== undefined ? variableData.missingDates : [];
    },
    variableMinDate() {
      let variableData = this.monthConfiguration;
      return variableData !== undefined ? variableData.minDate : null;
    },
    variableMaxDate() {
      let variableData = this.monthConfiguration;
      return variableData !== undefined ? variableData.maxDate : null;
    },
    variableYearRange() {
      let variableData = this.monthConfiguration;
      return variableData !== undefined ? variableData.availableYears : null;
    },
    variableFocusDate() {
      let variableData = this.monthConfiguration;
      return variableData !== undefined ? variableData.focusStartDate : null;
    },
    modalTitle() {
      let title = '';
      if (this.propFormData !== null) {
        let variableConfiguration = this.propFormData.variableConfiguration;
        title = variableConfiguration.getNameI18N(this.$i18n.locale)
      }
      return title
    },
    modalTitle2Objects() {
      let items = {};
      if (this.propFormData !== null)
        items[this.$t('InputSearchFormLabelTitle.Month')] = this.propFormData.monthYear;
      return items;
    },
    plotVariableNameUnit() {
      let title = this.modalTitle;

      let unit = '';
      if (this.propFormData !== null) {
        let variableConfiguration = this.propFormData.variableConfiguration;
        unit = variableConfiguration.unit;
      }

      if (unit !== '')
        title = `${title} [${unit}]`
      return title;
    },
    plotYAxis() {
      return this.plotVariableNameUnit;
    },
    plotTitle() {
      return this.plotVariableNameUnit;
    },
    plotFileName() {
      let name = 'monitoring';
      if (this.propFormData !== null) {
        let variableConfiguration = this.propFormData.variableConfiguration;
        name = `${name}_${this.propFormData.variable}_${this.dateRangeStartStr}_${this.dateRangeEndStr}`;
      }
      return name;
    },
    plotLabels() {
      let labels = [];
      if (this.plotData !== null)
        labels = this.plotData.labels !== undefined ? this.plotData.labels : [];

      let labelsTransform = [];
      labels.forEach(value => {
        let newValue = yearMonthStrToMonthYearI18N(value, this.$i18n);
        labelsTransform.push(newValue);
      });
      return labelsTransform;
    },
    plotDatasets() {
      let datasets = [];
      let lineZero = [];

      if (this.plotData !== null && this.plotData !== undefined) {
        let averageData = this.plotData.features.areas;

        let areasSelectedCount = 2;
        if (this.propPlotLayers !== null && this.propPlotLayers !== undefined)
          areasSelectedCount = this.propPlotLayers.length;

        if (averageData !== undefined) {
          datasets.push({
            type: 'line',
            label: this.$tc('InputSearchFormLabelValue.Areas', areasSelectedCount), //Series name
            backgroundColor: 'rgba(42,169,233,0.5)',
            borderColor: '#2aa9e9',
            fill: false,
            tension: 0.2,
            pointStyle: 'circle',
            pointRadius: 4,
            pointHoverRadius: 8,
            data: averageData
          });

          //Add zero line to have colors for anomalies above and beyond
          //TODO: check whether to add this or not from data
          averageData.forEach((value) => {
            lineZero.push(0);
          });
        }

        let historicalMeanData = this.plotData.features.historic_mean;
        if (historicalMeanData !== undefined && historicalMeanData !== null && historicalMeanData.length > 0)
          datasets.push({
            type: 'line',
            label: this.$t('InputSearchFormLabelValue.HistoricMean'), //Series name
            backgroundColor: 'rgba(126, 26, 125, 0.5)',
            borderColor: 'rgb(126, 26, 125)',
            pointBorderWidth: 1,
            borderWidth: 0, // No line
            fill: false,
            tension: 0.2,
            pointStyle: 'rectRot',
            pointRadius: 6,
            pointHoverRadius: 8,
            data: historicalMeanData
          });
      }

      /**
       * Add zero-line to simulate annotation to fill plot with color above and beyond
       * The line will be then hidden from the tooltip and from the legend
       */
      if (this.propFormData !== null && this.propFormData.variableConfiguration !== null) {
        let backgroundColorAbove = this.propFormData.variableConfiguration.getBackgroundAboveRGBA(0.15);
        let backgroundColorBelow = this.propFormData.variableConfiguration.getBackgroundBelowRGBA(0.15);

        if (backgroundColorAbove !== null && backgroundColorBelow !== null) {
          if (lineZero.length > 0) {
            datasets.push({
              type: 'line',
              data: lineZero,
              fill: {
                target: 'end',
                below: backgroundColorAbove, // Read from variable configuration
              },

              //Hidden line
              label: constants.LABELS.NO_LABEL,
              pointBorderWidth: 0,
              borderWidth: 0, // No line
              pointRadius: 0,
              pointHoverRadius: 0
            });

            datasets.push({
              type: 'line',
              data: lineZero,
              fill: {
                target: 'start',
                above: backgroundColorBelow // Read from variable configuration
              },
              showLine: false,
              hidden: false,
              //Hidden line
              label: constants.LABELS.NO_LABEL,
              pointBorderWidth: 0,
              borderWidth: 0,
              pointRadius: 0,
              pointHoverRadius: 0,
              borderColor: 'transparent'
            });
          }
        }
      }

      return datasets;
    },
    dateRangeStart() {
      return this.dateRange !== null && this.dateRange.length === 2 ? this.dateRange[0] : null;
    },
    dateRangeEnd() {
      return this.dateRange !== null && this.dateRange.length === 2 ? this.dateRange[1] : null;
    },
    currentDateStartStr() {
      return this.currentDateStart !== null ?
          yearMonthToStr(this.currentDateStart.year, this.currentDateStart.month + 1)
          : null;
    },
    currentDateEndStr() {
      return this.currentDateEnd !== null ?
          yearMonthToStr(this.currentDateEnd.year, this.currentDateEnd.month + 1)
          : null;
    },
    dateRangeStartStr() {
      return this.dateRangeStart !== null ?
          yearMonthToStr(this.dateRangeStart.year, this.dateRangeStart.month + 1)
          : null;
    },
    dateRangeEndStr() {
      return this.dateRangeEnd !== null ?
          yearMonthToStr(this.dateRangeEnd.year, this.dateRangeEnd.month + 1)
          : null;
    },
    isDateRangeModified() {
      return this.dateRangeStartStr === null ||
          this.dateRangeEndStr === null ||
          this.currentDateStartStr !== this.dateRangeStartStr ||
          this.currentDateEndStr !== this.dateRangeEndStr;
    },
    isUpdateButtonDisabled() {
      if (this.dateRangeStart === null || this.dateRangeEnd === null)
        return true;

      return this.loading.data === true || this.isDateRangeModified === false
    }
  },
  methods: {
    getVisualizationComponentI18NLabel,
    descriptionLabelI18NGet() {
      let params = {variable: this.modalTitle};

      let labelPostfix = 'area_one';
      let labelTooltipContent = '&nbsp;';

      if (this.propPlotLayers !== null && this.propPlotLayersCount !== null) {
        let selectedLayerCount = this.propPlotLayers.length;

        if (selectedLayerCount > 0 && this.propPlotLayersCount > 0) {
          if (selectedLayerCount === 1) {
            labelPostfix = 'area_one';

            if (this.propPlotLayerNames !== null) {
              labelTooltipContent = this.propPlotLayerNames.toString();
              labelTooltipContent = labelTooltipContent.replaceAll('"', "&#34;");
            }
          } else if (selectedLayerCount < this.propPlotLayersCount) {
            labelPostfix = 'area_many'
            params.area_count = selectedLayerCount;
          } else
            labelPostfix = 'area_all'
        }
      }

      params.tooltip_content = labelTooltipContent;
      return this.$t(`VisualizationPlot.monitoring.${labelPostfix}`, params);
    },
    dateRangeOnUpdate() {
      this.loading.data = true;
      this.loading.plot = true;

      ApiConnection.postMonitoringTimeseries(
          this.propFormData.variable,
          this.dateRangeStartStr,
          this.dateRangeEndStr,
          this.propPlotLayers
      ).then(response => {
        response.json().then(output => {
          //Update plot data
          this.plotData = output;

          //Update current date range
          this.currentDateStart = this.dateRangeStart;
          this.currentDateEnd = this.dateRangeEnd;
        });
      }).catch((error) => {
        //Send error to parent
        this.$emit('onTimeSeriesDataError', GroupErrorTranslation.t(error.message, this.$i18n));
      }).finally(() => {
        this.loading.data = false;
        this.loading.plot = false;
        this.$emit('afterPlotDataLoading');
      });
    },
    modalOpen() {
      this.modalStatus = true;
      this.$emit('onOpenModal');
    },
    modalOnClose() {
      this.modalStatus = false;
      this.$emit('onCloseModalClick');
    },
    downloadImageButtonOnClick() {
      this.plotDownload = !this.plotDownload;
    },
    downloadFileButtonOnClick() {
      this.loading.data = true;
      this.loading.file = true;

      ApiConnection.postMonitoringTimeseriesDownload(
          this.propFormData.variable,
          this.dateRangeStartStr,
          this.dateRangeEndStr,
          this.propPlotLayers
      ).then(response => {
        let fileName = response.headers.get('filename');
        if (fileName === null)
          fileName = `${this.plotFileName}.csv`;

        response.blob().then(data => {
          /*Create link with BLOB data to download*/
          downloadFileFromAElement(fileName, data);
        });
      }).catch((error) => {
        //Send error to parent
        this.$emit('onTimeSeriesDataError', GroupErrorTranslation.t(error.message, this.$i18n));
      }).finally(() => {
        this.loading.data = false;
        this.loading.file = false;
      });
    },
    tooltipsDispose() {
      disposeTooltipsByClass('.tooltip-modal-init');
    },
    tooltipsSetup() {
      this.tooltipsDispose();
      setupTooltipsByClass('.tooltip-modal-init');
    },
    offCanvasAreasShowOnClick() {
      this.offCanvasAreas.show = true;
    },
    offCanvasAreasOnHidden() {
      this.offCanvasAreas.show = false;
    }
  }
}
</script>

<template>
  <ModalWindow prop-id="modalGraph"
               prop-size="lg"
               :prop-static="true"
               :prop-status="modalStatus"
               @on-close-modal-click="modalOnClose">
    <template v-slot:title>
        <span class="h5 text-primary pe-1 fw-semibold">
          {{ $t(getVisualizationComponentI18NLabel(constants.COMPONENTS.MONITORING)) }}
        </span>{{ this.modalTitle }}
    </template>
    <div class="row">
      <div class="col mb-1">
        <p class="fw-light lh-sm">
          <span v-html="descriptionLabelI18NGet()"></span>
          <span v-html="$t('VisualizationPlot.monitoring.download')"></span>
        </p>
      </div>
    </div>
    <!-- Filters -->
    <div class="row mb-1">
      <div class="col-12 offset-0 col-lg-10 offset-lg-1">
        <div class="row">
          <div class="col-12 col-lg-9 mb-2">
            <div class="row">
              <label for="yearSelectPlot" class="col-3 col-form-label">{{
                  $t('InputSearchFormLabelTitle.Range')
                }}</label>
              <div class="col-9">
                <VueDatePicker
                    ref="datePickerRange"
                    v-model="dateRange"
                    format="MMM/yyy"
                    month-picker
                    year-picker
                    auto-apply
                    range
                    multi-calendars
                    prevent-min-max-navigation
                    :disabled="loading.data === true"
                    :teleport="'#dashboardCalendar'"
                    :start-date="variableFocusDate"
                    :min-date="variableMinDate"
                    :max-date="variableMaxDate"
                    :year-range="variableYearRange"
                    :allowed-dates="variableAvailableDates"
                    :disabled-dates="variableMissingDates"
                    :locale="this.$i18n.locale"
                    :format-locale="localeToFuncLocale(this.$i18n.locale)"
                    :clearable="false"
                />
              </div>
            </div>
          </div>
          <div class="col-12 col-lg-3 text-center">
            <button type="submit" class="btn btn-success w-100"
                    :disabled="isUpdateButtonDisabled"
                    @click="dateRangeOnUpdate">
              <div v-if="loading.plot === true" class="spinner-grow spinner-grow-sm me-2" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
              <i v-else class="bi pe-2"
                 :class="{
                      'bi-graph-up': isDateRangeModified === true,
                      'bi-check-circle-fill': isDateRangeModified === false,
                    }">
              </i>
              <span>{{ $t('ExploreButtonSearchForm.Update') }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
    <LineTimeSeries
        :prop-title="plotTitle !== undefined && plotTitle !== null ?  plotTitle : ''"
        :prop-labels="plotLabels"
        :prop-datasets="plotDatasets"
        :prop-download-toggle="plotDownload"
        :prop-download-file-name="plotFileName"
        :prop-x-axis-label="$t('InputSearchFormLabelTitle.Month')"
        :prop-y-axis-label="plotYAxis"
    />
    <template v-slot:footer>
      <div class="btn-group dropup">
        <button type="button" class="btn btn-success dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false"
                :disabled="loading.data === true">
          <div v-if="loading.file === true" class="spinner-grow spinner-grow-sm me-2" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          <i class="bi bi-download pe-2" v-else></i>
          <span class="pe-1">{{ $t('buttons.downloadData') }}</span>
        </button>
        <ul class="dropdown-menu">
          <li>
            <button class="dropdown-item" role="link" @click="downloadImageButtonOnClick"
                    :disabled="loading.data === true">
              <i class="bi bi-image pe-2"></i>{{ $t('buttons.downloadDataImage') }}
            </button>
          </li>
          <li>
            <hr class="dropdown-divider">
          </li>
          <li>
            <button class="dropdown-item" role="link" @click="downloadFileButtonOnClick"
                    :disabled="loading.data === true">
              <i class="bi bi-file-earmark-spreadsheet pe-2"></i>{{ $t('buttons.downloadDataCSV') }}
            </button>
          </li>
        </ul>
      </div>
    </template>
    <OffCanvas
        prop-id="plotMonitoringOffCanvas"
        :prop-title="$tc('InputSearchFormLabelValue.Areas', propPlotLayerNames.length)"
        :prop-show="offCanvasAreas.show"
        prop-position="end"
        :prop-hide-close-button="false"
        @on-canvas-hidden="offCanvasAreasOnHidden">
      <table class="table table-sm table-hover table-bordered table-striped">
        <tbody>
        <tr v-for="(area, index) in propPlotLayerNames">
          <td class="fw-semibold text-end">{{ index + 1 }}</td>
          <td>{{ area }}</td>
        </tr>
        </tbody>
      </table>
    </OffCanvas>
  </ModalWindow>
</template>